import { inject, Injectable } from '@angular/core';
import { GtmService, SnowplowService, TrackingEvents, WindowRefService } from '@sirius/common';
import { Page } from '../../models/page.model';
import { createPage, createUser } from '@urweb/snowtype';
import { addGlobalContexts, clearGlobalContexts } from '@snowplow/browser-tracker';
import { TrackingCommerceService, TrackingCommerceSnowplowService } from 'commerce/services';
import { LoggedInStateTypes, loggedIStatenQueryParam } from '../../models/logged-in-states.model';
import { ActivatedRoute, Params } from '@angular/router';
import { UtilService } from '../../services';

@Injectable({
    providedIn: 'root',
})
export class PageTrackingService {
    private _rawPageLinks: HTMLAnchorElement[] = [];

    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);
    private _trackingCommerceService = inject(TrackingCommerceService);
    private _trackingCommerceSnowplowService = inject(TrackingCommerceSnowplowService);
    private _windowRefService = inject(WindowRefService);
    private _utilService = inject(UtilService);

    private get Window() {
        return this._windowRefService.NativeWindow;
    }

    trackPage(page: Page | undefined, history: string[], activatedRoute: ActivatedRoute) {
        if (!this.Window) {
            return;
        }

        const curUrlIdx = history.length - 1;
        const prevUrlIdx = history.length - 2;
        const user = page?.user;

        const cfg = {
            ...page?.content.tracking.pageView?.parameters,
            page_location: history[curUrlIdx],
            page_referrer: history[prevUrlIdx] || this.Window.document.referrer,
            user_is_logged_in: Boolean(user),
            has_marketplace_access: Boolean(user?.hasMarketplaceAccess),
        };

        this._gtmService.track(TrackingEvents.gtm['pageView']([cfg]));

        const globalTracking = [];
        const pageView = createPage({
            language: page?.content.tracking.pageView?.parameters?.site_language || 'null',
            type: page?.content.tracking.pageView?.parameters?.content_group || 'null',
            locale: null,
        });

        globalTracking.push(pageView);

        if (user) {
            const userCfg = createUser({
                company_name: user?.companyName || null,
                has_marketplace_access: user?.hasMarketplaceAccess || null,
                email: page?.user?.userName || 'null', // userName is the email
            });

            globalTracking.push(userCfg);
        }

        clearGlobalContexts();
        addGlobalContexts(globalTracking);
        this._snowplowService.track('pageView', pageView);
        this.trackLoggedInState(activatedRoute);

        // Special tracking for ecommerce as it doesn't follow the same pattern as regular tracking service.
        if (page?.content?.ecommerceViewTrackingObject) {
            this._trackingCommerceService.trackEvent(page.content.ecommerceViewTrackingObject);
        }
        if (this.Window['pdt']) {
            this.Window['pdt']('sendPageView');
        }
        if (!page?.content.tracking.itemView) {
            return;
        }

        this._gtmService.track(
            TrackingEvents.gtm['itemView']([
                {
                    ...page?.content.tracking.itemView.parameters,
                },
            ]),
        );
    }

    trackTextCopiedGtm() {
        const text = this.Window?.getSelection()?.toString().substring(0, 150);
        const cfg = {
            text: text,
            length: text?.length,
        };

        this._gtmService.track(TrackingEvents.gtm['clipboardCopy']([cfg]));
    }

    trackRawLinkClicked(target: EventTarget | null): void {
        if (!target || !(target instanceof HTMLElement)) {
            return;
        }

        const anchorEl = target.closest('a');
        if (!anchorEl) {
            return;
        }

        this._rawPageLinks = this.getRawPageLinks();

        let idx = -1;
        for (let i = 0; i < this._rawPageLinks.length; i++) {
            if (anchorEl.isEqualNode(this._rawPageLinks[i])) {
                idx = i;

                break;
            }
        }

        if (idx === -1) {
            return;
        }

        const tagPrefix = 'sirius-';
        const parentSiriusEl = this._utilService
            .closestTagStartsWith(anchorEl, tagPrefix)
            ?.tagName.toLowerCase();

        const cfg = {
            category: 'other',
            identifier: parentSiriusEl ? `${parentSiriusEl.split(tagPrefix)[1]}/${idx + 1}` : '',
            outbound: anchorEl.target ? anchorEl.target === '_blank' : false,
            target_url: anchorEl.href,
        };

        this._snowplowService.track('linkClick', cfg);
    }

    private trackLoggedInState(activatedRoute: ActivatedRoute) {
        // Check if the query param given from backend in the return URL exists
        activatedRoute.queryParams.subscribe((params: Params) => {
            const loggedInState = params[loggedIStatenQueryParam];
            if (!loggedInState) {
                return;
            }

            if (loggedInState === LoggedInStateTypes.LoggedOut) {
                this._trackingCommerceSnowplowService.trackLogout();
            } else {
                console.error('Invalid logged in query parameter state');
                return;
            }
        });
    }

    private getRawPageLinks(): HTMLAnchorElement[] {
        const links: HTMLAnchorElement[] = [];
        document.querySelectorAll('a').forEach((x: HTMLAnchorElement) => {
            if (!('siriusLink' in x.dataset) && x.href) {
                links.push(x);
            }
        });

        return links;
    }
}
